import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Plus, Minus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ECOSYSTEM } from "@/data/content";

function DivisionCard({ d, i, dark }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`ecosystem-card-${i + 1}`}
      className={`group relative flex flex-col h-full p-8 lg:p-9 border transition-all duration-300 hover:-translate-y-1 overflow-hidden ${
        dark
          ? "bg-white/[0.03] border-white/10 hover:border-gold/50"
          : "bg-white border-oxford/10 hover:border-gold/60 hover:shadow-[0_24px_50px_-24px_rgba(5,19,35,0.25)]"
      }`}
    >
      <span className="absolute top-0 left-0 h-[2px] w-0 bg-gold transition-all duration-500 group-hover:w-full" />
      <div className="flex items-center gap-3">
        <img src="/brand/isotipo-gold.webp" alt="" className="h-12 w-12" />
        <span className="font-display text-[10px] font-semibold tracking-[0.3em] text-gold/70">0{i + 1}</span>
      </div>
      <h3 className={`mt-6 font-display text-lg font-semibold uppercase tracking-wide ${dark ? "text-white" : "text-oxford"}`}>{d.name}</h3>
      <p className="mt-1.5 font-display text-[11px] font-medium tracking-[0.14em] uppercase text-gold">{d.tagline}</p>
      <p className={`mt-4 text-sm leading-relaxed ${dark ? "text-steel" : "text-carbon/70"}`}>{d.desc}</p>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-5 pt-5 border-t border-gold/30">
              <p className={`font-display text-sm font-semibold ${dark ? "text-white" : "text-oxford"}`}>{d.detailTitle}</p>
              <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-steel" : "text-carbon/70"}`}>{d.detail}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`ecosystem-toggle-${i + 1}`}
        aria-expanded={open}
        className={`mt-auto pt-6 inline-flex items-center gap-2 w-fit font-display text-[10px] font-semibold tracking-[0.22em] uppercase transition-colors duration-300 ${
          dark ? "text-white/70 hover:text-gold" : "text-oxford/70 hover:text-gold"
        }`}
      >
        {open ? "Ver menos" : "Ver más"}
        {open ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

export default function EcosystemGrid({ dark = false }) {
  return (
    <div data-testid="ecosystem-grid" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {ECOSYSTEM.map((d, i) => (
        <Reveal key={d.name} delay={Math.min(i * 0.06, 0.36)} className="h-full">
          <DivisionCard d={d} i={i} dark={dark} />
        </Reveal>
      ))}
      <Reveal delay={0.4} className="h-full">
        <Link
          to="/contacto"
          data-testid="ecosystem-cta-card"
          className="group relative flex flex-col justify-between h-full min-h-[240px] p-8 lg:p-9 bg-oxford diagonal-lines border border-oxford transition-all duration-300 hover:-translate-y-1"
        >
          <img src="/brand/isotipo-gold.webp" alt="" className="absolute right-6 top-6 h-10 w-10 opacity-40" />
          <h3 className="font-display text-lg font-semibold text-white leading-snug max-w-[220px]">
            Un ecosistema. <span className="text-gold">Un solo aliado estratégico.</span>
          </h3>
          <span className="mt-8 inline-flex items-center gap-2 font-display text-[10px] font-semibold tracking-[0.22em] uppercase text-gold">
            Hablemos <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </Link>
      </Reveal>
    </div>
  );
}
